import Button from '@mui/material/Button';
import clsx from 'clsx';
import _orderBy from 'lodash.orderby';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { uploadFile } from '../../api';
import config from '../../config';
import { handleApiGlobalError } from '../../helpers/asyncActions';
import { useGlobalContext } from '../../stores/global';
import YesNoPrompt from '../Modals/YesNoPrompt';
import './GridUtils';
import styles from './MultipleFileLoader.module.scss';

const { apiHost } = config;

type LocalFile = {
  uri: string;
  reference: string;
  mime?: string;
  type?: string;
  name?: string;
};

type Layout = {
  uri: string;
  reference: string;
  mime?: string | undefined;
  type?: string | undefined;
  name?: string | undefined;
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}[];

type Type = 'images' | 'videos';

interface Props {
  type: Type;
  onChange?: (files: LocalFile[]) => void;
  defaultFiles?: LocalFile[];
}

const typeMap = {
  images: {
    plural: 'imágenes',
    accept: 'image/*',
    subtext: 'O Arrástralas aquí',
  },
  videos: {
    plural: 'videos',
    accept: 'video/*',
    subtext: 'O Arrástralos aquí',
  },
};

function getFormDataType(type: Type) {
  if (type === 'images') {
    return 'image';
  } else if (type === 'videos') {
    return 'video';
  }

  return '';
}

function orderLayout(layout: any) {
  return _orderBy(layout, ['y', 'x'], ['asc', 'asc']);
}

export default function MultipleImageLoader({
  defaultFiles = [],
  ...props
}: Props) {
  const { dispatch, isLoading } = useGlobalContext();
  const [imgToDelete, setImgToDelete] = useState<string>();
  const containerRef = useRef<{ cols: number; width: number }>({
    cols: 0,
    width: 0,
  });
  const [layout, setLayout] = useState<Layout>();

  const containerRefCb = useCallback((node) => {
    if (node) {
      const width = node.clientWidth;
      const cols = width < 450 ? 2 : 3;

      containerRef.current.width = width;
      containerRef.current.cols = cols;

      setLayout([
        ...defaultFiles
          .filter((el) => !!el)
          .map((file, idx) => ({
            i: `${file.reference}`,
            x: idx % cols,
            y: Math.floor(idx / cols),
            w: 1,
            h: 1,
            ...file,
          })),
      ]);
    }
  }, []);

  const noFiles = layout?.length === 0;

  const { accept, plural, subtext } = typeMap[props.type];

  useEffect(() => {
    const files = orderLayout(layout).map((el: any) => ({
      uri: el.uri,
      reference: el.reference,
      mime: el.mime,
      type: el.type,
      name: el.name,
    }));

    props.onChange?.(files);
  }, [layout]);

  const handleFileUpload = (files: File[]) => {
    if (isLoading) {
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    const filePromises = files.map((file) => {
      const bodyFormData = new FormData();
      bodyFormData.append('file', file);

      return uploadFile(bodyFormData);
    });

    Promise.all(filePromises)
      .then((responses) =>
        responses.map((response) => ({
          reference: response.data.data?.reference,
          uri: response.data.data?.uri,
          mime: response.data.data?.mime,
          name: response.data.data?.name,
          type: getFormDataType(props.type),
        }))
      )
      .then((newFiles) =>
        setLayout((layout) => [
          ...layout!,
          ...newFiles.map((file, idx) => ({
            i: `${file.reference}`,
            x: (layout!.length + idx) % containerRef.current.cols,
            y: Math.floor((layout!.length + idx) / containerRef.current.cols),
            w: 1,
            h: 1,
            ...file,
          })),
        ])
      )
      .catch(handleApiGlobalError.bind(null, dispatch))
      .finally(() => dispatch({ type: 'SET_LOADING', payload: false }));
  };

  const removeFile = (fileReference: string) => {
    setLayout([...layout!.filter((el) => el.i !== fileReference)]);
  };

  //function to know where the touch is
  const scrollTouch = (e: any) => {
    let modal = document.getElementById('modal-container') || {
      clientWidth: 0,
      scrollTop: 0,
    };
    let widthScreen = modal.clientWidth;
    let coordTouch = e.touches[0].clientY;
    if (coordTouch - widthScreen >= 190) {
      modal.scrollTop += 30;
    }
    if (widthScreen - coordTouch >= 190) {
      modal.scrollTop -= 30;
    }
  };

  return (
    <div
      onTouchMove={(e) => {
        scrollTouch(e);
      }}
      className={styles.container}
      ref={containerRefCb}
    >
      {layout && (
        <>
          {!noFiles && (
            <GridLayout
              layout={layout}
              compactType="horizontal"
              width={containerRef.current.width}
              cols={containerRef.current.cols}
              isResizable={false}
              onLayoutChange={(gridLayout: any[]) => {
                const newLayout = gridLayout.map((glItem) => {
                  const layoutItem = layout.find(
                    (el) => el.reference === glItem.i
                  )!;
                  return {
                    ...layoutItem,
                    x: glItem.x,
                    y: glItem.y,
                  };
                });

                setLayout(orderLayout(newLayout));
              }}
            >
              {layout.map((el, idx) => (
                <div
                  key={el.reference}
                  className={styles.image}
                  data-name={el.name}
                  style={{
                    backgroundImage: `url(${apiHost}/upload/info/thumbnail/${el.reference})`,
                  }}
                >
                  <button
                    type="button"
                    className={styles.imagesRemoveIcon}
                    onTouchStart={() => setImgToDelete(el.reference)}
                    onClick={() => setImgToDelete(el.reference)}
                  >
                    X
                  </button>
                  <span>{idx + 1}</span>
                </div>
              ))}
            </GridLayout>
          )}

          <div className={clsx(styles.placeholder, noFiles && styles.noFiles)}>
            <input
              type="file"
              onChange={(evt) => {
                const files = evt.currentTarget.files;

                if (!files) return;

                const parsedFiles = Array.from(files);
                handleFileUpload(parsedFiles);
              }}
              className={styles.input}
              multiple
              accept={accept}
            />
            <Button variant="contained" color="primary" size="small">
              Agrega {noFiles ? plural : 'más'}
            </Button>
            {!noFiles ? null : <p className={styles.text}>{subtext}</p>}
          </div>
        </>
      )}

      <YesNoPrompt
        open={Boolean(imgToDelete)}
        title="Eliminar imagen"
        text="Seguro que desea eliminar esta imagen?"
        onClose={() => setImgToDelete(undefined)}
        onYes={() =>
          imgToDelete && (removeFile(imgToDelete), setImgToDelete(undefined))
        }
      />
    </div>
  );
}
